import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { sitesApi, type Widget } from '../api/client';

export default function SiteWidgets() {
  const { siteId } = useParams<{ siteId: string }>();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'quiz' | 'poll'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'draft' | 'archived'>('all');

  const { data: siteData, isLoading: loadingSite } = useQuery({
    queryKey: ['site', siteId],
    queryFn: () => sitesApi.get(siteId!),
    enabled: !!siteId,
  });

  const { data: widgetsData, isLoading: loadingWidgets } = useQuery({
    queryKey: ['site-widgets', siteId],
    queryFn: () => sitesApi.getWidgets(siteId!),
    enabled: !!siteId,
  });

  const site = siteData?.site;
  const widgets = widgetsData?.widgets || [];

  // Filter widgets based on search and filters
  const filteredWidgets = useMemo(() => {
    return widgets.filter((widget) => {
      // Type filter
      if (typeFilter !== 'all' && widget.type !== typeFilter) {
        return false;
      }

      // Status filter
      if (statusFilter !== 'all' && widget.status !== statusFilter) {
        return false;
      }

      // Search filter
      if (search) {
        const searchLower = search.toLowerCase();
        let content: { hook_title?: string; question?: string } = {};
        try {
          content = JSON.parse(widget.content);
        } catch {}

        const title = content.hook_title || content.question || '';
        const matchesTitle = title.toLowerCase().includes(searchLower);
        const matchesUrl = widget.source_url.toLowerCase().includes(searchLower);
        const matchesId = widget.id.toLowerCase().includes(searchLower);

        if (!matchesTitle && !matchesUrl && !matchesId) {
          return false;
        }
      }

      return true;
    });
  }, [widgets, search, typeFilter, statusFilter]);

  if (loadingSite) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  if (!site) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">Site not found</p>
        <Link to="/sites" className="btn btn-primary">
          Back to Sites
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <Link to={`/sites/${siteId}`} className="text-gray-400 hover:text-gray-600">
          ← Site Settings
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">{site.domain} - Widgets</h1>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search by title, URL, or ID..."
              className="input w-full"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <select
              className="input w-auto"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as 'all' | 'quiz' | 'poll')}
            >
              <option value="all">All Types</option>
              <option value="quiz">Quiz</option>
              <option value="poll">Poll</option>
            </select>
            <select
              className="input w-auto"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'draft' | 'archived')}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
      </div>

      {/* Widgets List */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {filteredWidgets.length} widget{filteredWidgets.length !== 1 ? 's' : ''}
            {(search || typeFilter !== 'all' || statusFilter !== 'all') && (
              <span className="text-gray-500 font-normal ml-2">
                (filtered from {widgets.length})
              </span>
            )}
          </h2>
        </div>

        {loadingWidgets ? (
          <div className="text-center py-8 text-gray-500">Loading widgets...</div>
        ) : filteredWidgets.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {widgets.length === 0
              ? 'No widgets generated yet. Use the WordPress plugin or API to create widgets.'
              : 'No widgets match your filters.'}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredWidgets.map((widget) => (
              <WidgetRow key={widget.id} widget={widget} siteId={siteId!} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function WidgetRow({ widget, siteId }: { widget: Widget; siteId: string }) {
  const statusColors = {
    active: 'bg-green-100 text-green-700',
    draft: 'bg-yellow-100 text-yellow-700',
    archived: 'bg-gray-100 text-gray-700',
  };

  const typeColors = {
    quiz: 'bg-purple-100 text-purple-800',
    poll: 'bg-blue-100 text-blue-800',
  };

  let content: { hook_title?: string; question?: string } = {};
  try {
    content = JSON.parse(widget.content);
  } catch {}

  const title = content.hook_title || content.question || 'Untitled Widget';

  return (
    <Link
      to={`/sites/${siteId}/widgets/${widget.id}`}
      className="flex items-center justify-between py-4 hover:bg-gray-50 -mx-4 px-4 transition-colors"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className={`px-2 py-0.5 text-xs font-medium rounded ${typeColors[widget.type as keyof typeof typeColors] || 'bg-gray-100'}`}>
            {widget.type}
          </span>
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[widget.status]}`}>
            {widget.status}
          </span>
        </div>
        <p className="font-medium text-gray-900 truncate">{title}</p>
        <p className="text-sm text-gray-500 truncate">{widget.source_url}</p>
        <p className="text-xs text-gray-400">
          {new Date(widget.created_at).toLocaleDateString()} · ID: {widget.id.slice(0, 8)}...
        </p>
      </div>
      <span className="text-gray-400 ml-4">→</span>
    </Link>
  );
}
